import * as cssVariables from '../sharedwithcss.ts';

/**
 * Snippet represents the geometry necessary to render a snippet of an image. It encodes
 * it as a set of coordinates on the underlying image (in percents), the original aspect ratio
 * the image was displayed at
 */
export class Snippet {
  left: number;
  top: number;
  right: number;
  bottom: number;
  aspectRatio: number;
  width: number;
  height: number;

  /**
   * Parse the provided coordinate and aspect ratio strings, and return a Snippet.
   * @param {string} coordStr The comma separated coordinates in a [0,100] percent
   * @param {string | null} [aspectRatioStr] A string encoded float, which may be null
   * @return {Snippet}
   */
  static parse(coordStr: string, aspectRatioStr: string | null) {
    const coords = Snippet.parseCoords(coordStr);

    let aspectRatio = parseFloat(aspectRatioStr);
    if (!aspectRatio || Number.isNaN(aspectRatio)) {
      aspectRatio = 1;
    }

    return new Snippet(
      coords.left,
      coords.top,
      coords.right,
      coords.bottom,
      aspectRatio,
    );
  }

  /**
   * Parse the provided coordinate and aspect ratio strings, and return a partial snippet.
   * @param {string} coordStr The comma separated coordinates in a [0,100] percent
   * @return {{left: number, top: number, right: number, bottom: number}}
   */
  static parseCoords(coordStr: string) {
    const defaults = {left: 0, top: 0, right: 1, bottom: 1};
    if (!coordStr) return defaults;

    const parts = coordStr.trim().split(/,/);
    if (parts.length !== 4) return defaults;

    const nums = parts.map((s: string) => parseFloat(s.trim()));
    if (nums.some((x: number) => Number.isNaN(x))) return defaults;

    return {
      left: nums[0] / 100,
      top: nums[1] / 100,
      right: nums[2] / 100,
      bottom: nums[3] / 100,
    };
  }

  /**
   * @param {number} left the left coordinate of the snippet bounding box, as a percent from 0 to 1
   * @param {number} top the top coordinate of the snippet bounding box, as a percent from 0 to 1
   * @param {number} right the right coordinate of the snippet bounding box, as a percent from 0 to 1
   * @param {number} bottom the bottom coordinate of the snippet bounding box, as a percent from 0 to 1
   * @param {number} aspectRatio the original aspectRatio of the document
   */
  constructor(left: number, top: number, right: number, bottom: number, aspectRatio: number) {
    this.left = left;
    this.top = top;
    this.right = right;
    this.bottom = bottom;
    this.aspectRatio = aspectRatio;

    // Subtract two pixels for the border
    this.width = cssVariables.asSnippetThumbnailWidth - 2;

    // First, calculate the aspect ratio of the snippet itself,
    // without the rendered aspect ratio.
    const snippetAspectRatio = (right - left) / (bottom - top);

    // Multiply the snippet aspect ratio by the displayed aspect ratio
    // of the image to get the actual aspect ratio.
    this.height = this.width / (snippetAspectRatio * aspectRatio);
  }

  /**
   * Return a dimension style suitable for Vue
   * @return {height: string, width: string}
   */
  style() {
    return {
      height: `${this.height.toFixed(1)}px`,
      width: `${this.width.toFixed(1)}px`,
    };
  }
}
